/**
 * Central de atendimento. Desktop: fila | conversa | cliente. Celular: uma
 * tela por vez (lista -> conversa -> painel do cliente por cima).
 */
import type { ConversaResumo, FiltroConversas } from "@ydchat/shared";
import { veOperacaoInteira } from "@ydchat/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Clock, MessagesSquare } from "lucide-react";
import { useState } from "react";
import { ContextoCliente } from "@/components/atendimento/contexto-cliente";
import { Conversa } from "@/components/atendimento/conversa";
import { ListaConversas } from "@/components/atendimento/lista-conversas";
import { Avatar } from "@/components/ui/avatar";
import { CartaoRolavel } from "@/components/ui/cartao-rolavel";
import { EstadoVazio } from "@/components/ui/estado-vazio";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useUsuario } from "@/lib/sessao";
import { duracaoCurta } from "@/lib/formatar";
import { useAgora } from "@/lib/use-agora";
import { useMidia } from "@/lib/use-midia";

export type Situacao = FiltroConversas["status"];
export type BuscaAtendimentos = {
  escopo?: FiltroConversas["escopo"];
  situacao?: Exclude<Situacao, "abertas">;
  /** Caixa de entrada (canal); sem ela, todas. */
  caixa?: string;
};

export function validarBuscaAtendimentos(s: Record<string, unknown>): BuscaAtendimentos {
  const e = s["escopo"];
  const st = s["situacao"];
  const cx = s["caixa"];
  return {
    ...(e === "sem_responsavel" || e === "todas" || e === "minhas" ? { escopo: e } : {}),
    ...(st === "acompanhamento" || st === "encerradas" || st === "retomada"
      ? { situacao: st }
      : {}),
    ...(typeof cx === "string" && /^[0-9a-f-]{36}$/i.test(cx) ? { caixa: cx } : {}),
  };
}

export function LayoutAtendimentos() {
  const busca = useSearch({ strict: false }) as BuscaAtendimentos;
  const params = useParams({ strict: false }) as { conversaId?: string };
  const navegar = useNavigate();
  // Gerência abre na operação inteira; vendedora, nos próprios atendimentos.
  const padrao = veOperacaoInteira(useUsuario().papel) ? "todas" : "minhas";
  const escopo = busca.escopo ?? padrao;

  return (
    <div className="flex h-full min-h-0">
      <div
        className={cn(
          "h-full w-full shrink-0 border-r border-neutral-200 md:w-[340px] lg:w-[380px]",
          params.conversaId && "hidden md:block",
        )}
      >
        <ListaConversas
          escopo={escopo}
          situacao={busca.situacao ?? "abertas"}
          caixa={busca.caixa}
          onCaixa={(cx) => void navegar({ to: ".", search: { ...busca, caixa: cx } })}
          onEscopo={(e) =>
            void navegar({ to: ".", search: { ...busca, escopo: e === padrao ? undefined : e } })
          }
          onSituacao={(st) =>
            void navegar({
              to: ".",
              search: { ...busca, situacao: st === "abertas" ? undefined : st },
            })
          }
        />
      </div>
      <div className={cn("h-full min-w-0 flex-1", !params.conversaId && "hidden md:block")}>
        <Outlet />
      </div>
    </div>
  );
}

/**
 * Área da conversa quando nenhuma está aberta: em vez de espaço vazio, o
 * resumo do turno (atalhos para as abas) e quem espera há mais tempo.
 */
export function SemConversaSelecionada() {
  const usuario = useUsuario();
  const gestor = veOperacaoInteira(usuario.papel);
  const escopo = gestor ? "todas" : "minhas";
  const navegar = useNavigate();
  const agora = useAgora(30_000);
  const abertas = useQuery({
    queryKey: ["conversas", escopo, "", "abertas", ""],
    queryFn: () => api.get<ConversaResumo[]>(`/conversas?escopo=${escopo}&status=abertas`),
    refetchInterval: 60_000,
  });
  const fila = useQuery({
    queryKey: ["conversas", "sem_responsavel", "", ""],
    queryFn: () => api.get<ConversaResumo[]>("/conversas?escopo=sem_responsavel&status=abertas"),
  });
  const retomar = useQuery({
    queryKey: ["conversas", "retomada", escopo, ""],
    queryFn: () => api.get<ConversaResumo[]>(`/conversas?escopo=${escopo}&status=retomada`),
  });

  const lista = abertas.data ?? [];
  const esperando = lista
    .filter((c) => c.aguardandoRespostaDesde)
    .sort((x, y) => x.aguardandoRespostaDesde!.localeCompare(y.aguardandoRespostaDesde!));
  const naoLidas = lista.reduce((t, c) => t + c.naoLidas, 0);
  const ir = (busca: BuscaAtendimentos) =>
    void navegar({ to: "/atendimentos", search: busca as never });

  const blocos: {
    rotulo: string;
    valor: number | undefined;
    destaque?: boolean;
    busca: BuscaAtendimentos;
  }[] = [
    {
      rotulo: gestor ? "Abertas" : "Minhas abertas",
      valor: abertas.data?.length,
      busca: { escopo },
    },
    {
      rotulo: "Esperando resposta",
      valor: abertas.data ? esperando.length : undefined,
      destaque: esperando.length > 0,
      busca: { escopo },
    },
    {
      rotulo: "Na fila",
      valor: fila.data?.length,
      destaque: !!fila.data?.length,
      busca: { escopo: "sem_responsavel" },
    },
    {
      rotulo: "Para retomar",
      valor: retomar.data?.length,
      destaque: !!retomar.data?.length,
      busca: { escopo, situacao: "retomada" },
    },
  ];

  return (
    <div className="rolagem-fina h-full overflow-y-auto bg-neutral-50/60">
      <div className="mx-auto flex min-h-full max-w-3xl flex-col justify-center gap-6 p-6 lg:p-10">
        <div>
          <h2 className="text-xl">Resumo do atendimento</h2>
          <p className="mt-1 text-sm text-neutral-500">
            {naoLidas
              ? `${naoLidas} mensagem(ns) não lida(s) ${gestor ? "na operação" : "nas suas conversas"}.`
              : "Nenhuma mensagem sem ler."}{" "}
            Use / para buscar e Ctrl+K para a busca geral.
          </p>
        </div>

        <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {blocos.map((b) => (
            <button
              key={b.rotulo}
              onClick={() => ir(b.busca)}
              className={cn(
                "rounded-xl border bg-white p-4 text-left transition-colors hover:border-gold-300",
                b.destaque ? "border-warning-500/40" : "border-neutral-200",
              )}
            >
              <dt className={cn("text-xs", b.destaque ? "text-warning-500" : "text-neutral-500")}>
                {b.rotulo}
              </dt>
              <dd className="mt-1 font-display text-3xl font-bold text-neutral-900">
                {b.valor ?? "–"}
              </dd>
            </button>
          ))}
        </dl>

        <CartaoRolavel
          titulo="Esperando há mais tempo"
          icone={Clock}
          contagem={abertas.data ? esperando.length : undefined}
          className="max-h-[22rem]"
        >
          {!esperando.length ? (
            <EstadoVazio
              icone={MessagesSquare}
              titulo="Ninguém esperando"
              descricao="Quando uma cliente escrever, ela aparece aqui por ordem de espera."
            />
          ) : (
            <ul className="divide-y divide-neutral-100">
              {esperando.slice(0, 20).map((c) => (
                <li key={c.id}>
                  <Link
                    to="/atendimentos/$conversaId"
                    params={{ conversaId: c.id }}
                    search={(s) => s}
                    className="flex items-center gap-3 px-4 py-2.5 text-neutral-900 hover:bg-neutral-50 hover:no-underline"
                  >
                    <Avatar nome={c.contato.nome} tamanho="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{c.contato.nome}</span>
                      <span className="block truncate text-xs text-neutral-500">
                        {c.ultimaMensagem?.texto ?? "Sem mensagens"}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs font-bold text-warning-500">
                      {duracaoCurta(c.aguardandoRespostaDesde!, agora)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CartaoRolavel>
      </div>
    </div>
  );
}

function lerPreferencia(): boolean {
  try {
    return localStorage.getItem("ydchat:contexto-aberto") !== "0";
  } catch {
    return true;
  }
}

export function TelaConversa() {
  const { conversaId } = useParams({ strict: false }) as { conversaId: string };
  // Painel fixo em telas largas (lembra a escolha); sobreposto nas menores.
  const [fixoAberto, setFixoAberto] = useState(lerPreferencia);
  const [sobrepostoAberto, setSobrepostoAberto] = useState(false);
  const conversa = useQuery({
    queryKey: ["conversa", conversaId, "dados"],
    queryFn: () => api.get<ConversaResumo>(`/conversas/${conversaId}`),
  });

  // Só uma versão do painel existe por vez (evita consultas em dobro).
  const telaLarga = useMidia("(min-width: 1280px)");
  const alternar = () => {
    if (telaLarga) {
      setFixoAberto((v) => {
        try {
          localStorage.setItem("ydchat:contexto-aberto", v ? "0" : "1");
        } catch {
          // armazenamento indisponível: só não lembra a preferência
        }
        return !v;
      });
    } else {
      setSobrepostoAberto((v) => !v);
    }
  };

  return (
    <div className="flex h-full min-h-0">
      <div className="min-w-0 flex-1">
        <Conversa conversaId={conversaId} onAlternarContexto={alternar} />
      </div>
      {conversa.data && telaLarga && fixoAberto && (
        <div className="h-full w-[340px] shrink-0 border-l border-neutral-200">
          <ContextoCliente conversa={conversa.data} onFechar={alternar} />
        </div>
      )}
      {conversa.data && !telaLarga && sobrepostoAberto && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <button
            aria-label="Fechar"
            className="absolute inset-0 bg-[rgba(28,24,21,0.45)]"
            onClick={() => setSobrepostoAberto(false)}
          />
          <div className="relative h-full w-full max-w-[380px] shadow-lg">
            <ContextoCliente conversa={conversa.data} onFechar={() => setSobrepostoAberto(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
